import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { HERO_IMAGES, TEAM_IMG, CATEGORY_IMAGES } from "../mock";
import HERO_LIGHT_BG_IMG from "../assets/hero-light-bg.jpg";
import {
  Sparkles, Users, Target, Share2, Zap, UserCog, ClipboardList, Handshake,
  FileCheck2, Building2, Globe2, Layers, ArrowRight, CheckCircle2
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-[#15348C]/[0.06] border border-[#15348C]/10 px-4 py-1.5 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-[#F57C20]"/>
    {children}
  </span>
);

const MOSAIC_AREAS = [
  { icon: Users, title: "Specialized Expertise", headline: "Access the Skills Your Project Requires", desc: "Engage experienced specialists with relevant expertise across emerging and enterprise technology domains." },
  { icon: Target, title: "Defined Project Scope", headline: "Clear Objectives from the Start", desc: "Engagements are aligned with specific project requirements, expected deliverables, technology needs, and business priorities." },
  { icon: Share2, title: "Knowledge Transfer", headline: "Knowledge That Stays with Your Team", desc: "Structured knowledge sharing, documentation, and guidance help internal teams gain practical understanding throughout the engagement." },
  { icon: Zap, title: "Faster Project Start", headline: "Get the Right Expertise When You Need It", desc: "A streamlined engagement process helps organizations access specialized expertise for time-bound projects and critical initiatives." }
];

const MOSAIC_COLORS = {
  blue: { badge: "bg-[#2F6FF0]", ring: "border-[#2F6FF0]/30", text: "text-[#2F6FF0]", panel: "bg-[#EEF3FF] border-[#15348C]/10", hover: "sg-card--blue" },
  teal: { badge: "bg-[#0F9D8B]", ring: "border-[#0F9D8B]/30", text: "text-[#0F9D8B]", panel: "bg-[#EAFBF6] border-[#0F9D8B]/10", hover: "sg-card--teal" },
  orange: { badge: "bg-[#F57C20]", ring: "border-[#F57C20]/30", text: "text-[#F57C20]", panel: "bg-[#FFF3E6] border-[#F57C20]/15", hover: "sg-card--orange" }
};

const APPROACH_CARDS = [
  { icon: UserCog, title: "Technology Specialists", desc: "Access experienced professionals with specialized expertise aligned with your project and technology requirements." },
  { icon: ClipboardList, title: "Project-Focused Engagement", desc: "Engagements are structured around defined objectives, project scope, deliverables, and expected outcomes." },
  { icon: Handshake, title: "Collaborative Knowledge Sharing", desc: "Specialists work alongside relevant teams, supporting practical learning and knowledge sharing during the engagement." },
  { icon: FileCheck2, title: "Structured Handover", desc: "Documentation, guidance, and relevant project knowledge are shared to support continuity after the engagement." }
];

const METRICS = [
  { icon: Building2, value: "50+", label: "Fortune & High-Growth Companies Served", desc: "Supporting organizations across diverse technology and business requirements." },
  { icon: Globe2, value: "25+", label: "Global Technology Partners", desc: "Access to expertise across leading technology platforms and domains." },
  { icon: Layers, value: "200+", label: "Corporate Learning Programs", desc: "Programs designed around specialized technology and organizational requirements." },
  { icon: Users, value: "100+", label: "Enterprise Trainers", desc: "Experienced professionals supporting specialized technology domains and enterprise programs." }
];

const NicheSkillProjectsPage = () => {
  const [Mosaic0, Mosaic1, Mosaic2, Mosaic3] = MOSAIC_AREAS.map(a => a.icon);
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundImage: `url(${HERO_LIGHT_BG_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="relative max-w-5xl mx-auto px-4 py-24 lg:py-28 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-white border border-[#15348C]/10 px-4 py-2 rounded-full mb-7 shadow-sm">
              <Sparkles size={14}/> NICHE SKILL PROJECTS
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold leading-[1.1] text-[#0B1E4D]">Specialized Expertise for Critical Technology Projects</h1>
            <p className="mt-5 text-gray-600 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
              When specialized skills are needed for a specific project or initiative, Skillsgain connects organizations with experienced technology specialists through structured engagements aligned with clearly defined project requirements.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                Talk to Our Experts <ArrowRight size={16}/>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mosaic — how SkillsGain delivers */}
      <section className="relative bg-[#F8F9FA] py-12 lg:py-14 overflow-hidden">
        <div
          className="absolute top-4 left-0 w-40 h-40 opacity-70 pointer-events-none hidden lg:block"
          style={{ backgroundImage: "radial-gradient(#CBD5E1 1.6px, transparent 1.6px)", backgroundSize: "16px 16px" }}
        />
        <div className="relative max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <Eyebrow>HOW SKILLSGAIN DELIVERS</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold leading-tight text-[#0B1E4D]">Specialized Expertise Aligned with Your Project Requirements</h2>
              <p className="mt-3 text-gray-600">From identifying the right expertise to supporting knowledge transfer, every engagement is aligned with your project scope, technology environment, and business priorities.</p>
              <span className="mt-3 inline-block w-10 h-0.5 rounded-full bg-[#2F6FF0]"/>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-5 gap-6 items-stretch">
            {/* 01 — Specialized Expertise (large feature card) */}
            <Reveal variant="up" className="lg:col-span-3">
              <div className="sg-card group relative rounded-2xl overflow-hidden h-full min-h-[300px] shadow-sm bg-[#0A1330] p-8 lg:p-10 flex flex-col">
                <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#2F6FF0]/20 rounded-full blur-3xl"/>
                <div className="relative flex items-center gap-3">
                  <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${MOSAIC_COLORS.blue.badge} text-white font-bold text-sm`}>01</span>
                  <span className="w-10 h-10 flex items-center justify-center rounded-full border border-white/40">
                    <Mosaic0 size={18} className="text-white"/>
                  </span>
                </div>
                <h3 className="relative mt-6 text-2xl lg:text-3xl font-bold text-white leading-tight">{MOSAIC_AREAS[0].title}</h3>
                <span className={`relative mt-4 w-10 h-0.5 block ${MOSAIC_COLORS.blue.badge}`}/>
                <div className="relative mt-4">
                  <div className="text-[#8BD835] text-sm font-semibold">{MOSAIC_AREAS[0].headline}</div>
                  <p className="mt-2 text-gray-300 text-sm leading-relaxed max-w-md">{MOSAIC_AREAS[0].desc}</p>
                </div>
              </div>
            </Reveal>

            {/* 02 & 03 — right-hand stacked cards */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <Reveal variant="up" delay={80}>
                <div className={`sg-card ${MOSAIC_COLORS.blue.hover} relative rounded-2xl overflow-hidden p-6 h-full min-h-[136px] border ${MOSAIC_COLORS.blue.panel}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${MOSAIC_COLORS.blue.badge} text-white font-bold text-sm`}>02</span>
                    <span className={`w-10 h-10 flex items-center justify-center rounded-full border bg-white ${MOSAIC_COLORS.blue.ring}`}>
                      <Mosaic1 size={18} className={MOSAIC_COLORS.blue.text}/>
                    </span>
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-[#15348C]">{MOSAIC_AREAS[1].title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{MOSAIC_AREAS[1].desc}</p>
                </div>
              </Reveal>
              <Reveal variant="up" delay={160}>
                <div className={`sg-card ${MOSAIC_COLORS.teal.hover} relative rounded-2xl overflow-hidden p-6 h-full min-h-[136px] border ${MOSAIC_COLORS.teal.panel}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${MOSAIC_COLORS.teal.badge} text-white font-bold text-sm`}>03</span>
                    <span className={`w-10 h-10 flex items-center justify-center rounded-full border bg-white ${MOSAIC_COLORS.teal.ring}`}>
                      <Mosaic2 size={18} className={MOSAIC_COLORS.teal.text}/>
                    </span>
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-[#15348C]">{MOSAIC_AREAS[2].title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{MOSAIC_AREAS[2].desc}</p>
                </div>
              </Reveal>
            </div>
          </div>

          {/* 04 — Faster Project Start (banner) */}
          <Reveal variant="up" delay={240}>
            <div className={`sg-card ${MOSAIC_COLORS.orange.hover} mt-6 relative rounded-2xl overflow-hidden border ${MOSAIC_COLORS.orange.panel} shadow-lg shadow-[#F57C20]/10`}>
              <div className="flex flex-col lg:flex-row items-center">
                <div className="flex-1 w-full p-5 lg:pl-8 lg:pr-6 lg:py-6">
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-lg ${MOSAIC_COLORS.orange.badge} text-white font-bold text-sm`}>04</span>
                    <span className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full border bg-white ${MOSAIC_COLORS.orange.ring}`}>
                      <Mosaic3 size={18} className={MOSAIC_COLORS.orange.text}/>
                    </span>
                    <h3 className="text-xl lg:text-2xl font-bold text-[#15348C]">{MOSAIC_AREAS[3].title}</h3>
                  </div>
                  <div className={`mt-2.5 text-sm font-semibold ${MOSAIC_COLORS.orange.text}`}>{MOSAIC_AREAS[3].headline}</div>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed max-w-lg">{MOSAIC_AREAS[3].desc}</p>
                  <div className="mt-4">
                    <Link to="/contact" className={`sg-btn-glow inline-flex items-center gap-2 ${MOSAIC_COLORS.orange.badge} hover:opacity-90 text-white font-semibold px-5 py-2.5 rounded-lg transition-opacity text-sm`}>
                      Talk to Our Experts <ArrowRight size={16}/>
                    </Link>
                  </div>
                </div>
                <div className="w-full lg:w-[55%] shrink-0">
                  <img src={TEAM_IMG} alt={MOSAIC_AREAS[3].title} className="w-full h-40 lg:h-36 object-cover"/>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Project engagement approach — image + 4 cards */}
      <section className="bg-white py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img src={CATEGORY_IMAGES.dev} alt="Specialized technology project engagement" className="rounded-2xl shadow-xl ring-1 ring-black/5 w-full h-[440px] object-cover"/>
          </Reveal>
          <Reveal variant="right" delay={80}>
            <Eyebrow>PROJECT ENGAGEMENT APPROACH</Eyebrow>
            <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Expertise That Supports Your Team and Project</h2>
            <p className="mt-3 text-gray-600 leading-relaxed">Niche Skill Projects provide focused expertise for specific initiatives while enabling internal teams to gain relevant knowledge throughout the engagement.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {APPROACH_CARDS.map(c => (
                <div key={c.title} className="sg-card flex gap-3.5 bg-white border border-gray-200/80 rounded-xl p-5 shadow-sm">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/12 flex items-center justify-center">
                    <c.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div>
                    <div className="font-bold text-[#15348C] text-sm">{c.title}</div>
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* By the numbers */}
      <section className="bg-[#FFFAF0] py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <Eyebrow>BY THE NUMBERS</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Trusted Across Specialized Technology Engagements</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {METRICS.map((m, i) => (
              <Reveal key={m.label} variant="up" delay={i * 80}>
                <div className="sg-card group bg-white border border-gray-200/80 border-t-4 border-t-transparent hover:border-t-[#6BBE32] rounded-2xl p-7 h-full shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#6BBE32]/12 flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                    <m.icon size={22} className="text-[#6BBE32] group-hover:text-white transition-colors"/>
                  </div>
                  <div className="text-4xl font-bold text-[#15348C] tracking-tight">{m.value}</div>
                  <div className="text-sm font-semibold text-[#15348C] mt-2">{m.label}</div>
                  <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why SkillsGain */}
      <section className="bg-[#F8F9FA] py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img
              src={HERO_IMAGES[1]}
              alt="Specialized expertise delivered through SkillsGain Niche Skill Projects"
              className="w-full h-[380px] object-cover rounded-2xl shadow-xl ring-1 ring-black/5"
            />
          </Reveal>
          <Reveal variant="right" delay={80}>
            <Eyebrow>WHY SKILLSGAIN</Eyebrow>
            <h2 className="mt-4 text-2xl lg:text-3xl font-bold text-[#15348C] leading-snug">Specialized Expertise When Your Project Needs It</h2>
            <p className="mt-5 text-gray-600 leading-relaxed">
              Some projects require capabilities that may not be readily available within internal teams. Skillsgain helps organizations access specialized expertise for focused technology initiatives and project requirements.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              Our Niche Skill Projects are structured around clearly defined objectives, relevant technology expertise, and collaboration with internal teams. Along with project support, engagements can include practical knowledge sharing, guidance, and structured handover.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              From AI and Generative AI to cloud, data, cybersecurity, and other emerging technologies, engagements can be aligned with the specific skills and expertise required for your project.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] rounded-3xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR TECHNOLOGY &amp; BUSINESS LEADERS</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Discuss Your Niche Skill Project</h2>
                  <p className="mt-4 text-gray-300 leading-relaxed">Tell us about your project requirements, technology environment, and the specialized expertise you need. Our team can help structure an engagement aligned with your objectives.</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-gray-300">
                    {["Specialized technology expertise", "Project-focused engagement", "Collaborative knowledge sharing", "Structured project handover"].map(t => (
                      <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#8BD835]"/>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                    Talk to Our Experts <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="border-2 border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/10 hover:border-[#8BD835] transition-colors">Discuss Your Project</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default NicheSkillProjectsPage;
